/**
 * Badge Card Component
 * Individual badge display card with icon, name, and earned date
 */

import { BadgeIcon } from './BadgeIcon';
import { BadgeTooltip } from './BadgeTooltip';
import { cn } from '../../lib/utils';
import type { BadgeDefinition, UserBadge } from '../../lib/types/badge';

interface BadgeCardProps {
  badge: BadgeDefinition;
  userBadge: UserBadge;
  className?: string;
  onClick?: () => void; // For future details view
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export function BadgeCard({
  badge,
  userBadge,
  className = '',
  onClick,
}: BadgeCardProps) {
  // Parse earned_at from ISO string or Timestamp
  let earnedDate: Date | null = null;
  if (userBadge.earned_at) {
    if (typeof userBadge.earned_at === 'string') {
      // Parse ISO 8601 string from API
      earnedDate = new Date(userBadge.earned_at);
      // Validate the date
      if (isNaN(earnedDate.getTime())) {
        earnedDate = null;
      }
    } else {
      // Handle Firestore Timestamp (for backward compatibility)
      earnedDate = userBadge.earned_at.toDate();
    }
  }
  const timeAgo = earnedDate ? formatTimeAgo(earnedDate) : null;

  return (
    <BadgeTooltip
      badgeName={badge.name}
      description={badge.description}
      earnedAt={earnedDate?.toISOString()}
    >
      <div
        className={cn(
          'group relative bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700',
          'hover:shadow-md hover:scale-105 transition-all duration-200 ease-in-out',
          'focus-within:ring-2 focus-within:ring-sky-500 focus-within:ring-offset-2',
          onClick && 'cursor-pointer',
          className
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        {/* Badge Icon */}
        <div className="flex justify-center mb-3">
          <BadgeIcon
            src={badge.icon_url}
            alt={badge.name}
            size="md"
            imageless={badge.imageless}
          />
        </div>

        {/* Badge Name */}
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center mb-1 truncate">
          {badge.name}
        </h3>

        {/* Earned Date */}
        {timeAgo && (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {timeAgo}
          </p>
        )}

        {/* Hover Effect Overlay */}
        {onClick && (
          <div className="absolute inset-0 bg-sky-500 bg-opacity-0 group-hover:bg-opacity-5 rounded-lg transition-all duration-200" />
        )}
      </div>
    </BadgeTooltip>
  );
}
